/**
 * @file RenderAnnotationFields.js
 * @description renders dynamic annotation fields
 *              for both acmg and generalFeature
 *              annotations
 * 
 *              if a handleDelete function is passed in,
 *              will render a RemoveIcon and return the
 *              fieldName onClick
 * 
 * important note:  shared by documentAnnotator and tableAnnotator
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Mar 24, 2022 11:42
 */
import { Fragment } from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { red } from '@mui/material/colors';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import Tooltip from '@mui/material/Tooltip';

import { findLabel, findHelpText, findOptions, findWidth } from 'src/@common/schemaUtilities';
import { arrayExists, objectExists, stringExists } from 'src/@common/utilities';

import DiseaseField from 'src/components/fieldComponents/DiseaseField';
import GeneSymbolField from 'src/components/fieldComponents/GeneSymbolField';
import PatientHPOField from 'src/components/fieldComponents/PatientHPOField';
import TranscriptField from 'src/components/fieldComponents/TranscriptField';
import VeriomeBooleanField from 'src/components/fieldComponents/VeriomeBooleanField';
import VariantObjArrayField from 'src/components/fieldComponents/VariantObjArrayField/VariantObjArrayFieldContainer';
import VariantObjField from 'src/components/fieldComponents/VariantObjField/VariantObjFieldContainer';
import VeriomeSelectField from 'src/components/fieldComponents/VeriomeSelectField';
import VeriomeNumberField from 'src/components/fieldComponents/VeriomeNumberField';
import VeriomeTextField from 'src/components/fieldComponents/VeriomeTextField';

function RenderAnnotationFields(props) {
    const {
        annotationType,
        biocurations,
        fieldErrors,
        formFields,
        fieldSchema,
        dataLoading,
        diseaseOptions,
        handleBlur,
        handleChange,
        handleDelete,  // optional, used only if the field can be deleted
        handleVariantObjAdd,
        handleVariantObjBlur,
        handleVariantObjDelete,
        handleGDotLookup,
    } = props;

    return (
        <Grid container
            spacing={1}
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
        >
            {
                formFields && objectExists(formFields) &&
                Object.keys(formFields).map((fieldName, index) => {

                    if (stringExists(fieldName)) {
                        const fieldValue = formFields[fieldName];

                        let fieldAttributes = null;

                        switch (fieldName) {
                            // render fields by name

                            case 'disease':
                                return (<Fragment key={`${fieldName}-field-${index}`}>

                                    <DiseaseField
                                        key={`${fieldName}-field-${index}`}
                                        fieldName={'disease'}
                                        fieldLabel={findLabel(fieldSchema, 'disease')}
                                        currentValue={formFields.disease ? formFields.disease : { name: '', id: '' }}
                                        diseaseCurations={diseaseOptions}
                                        fieldErrors={fieldErrors}
                                        helpText={findHelpText(fieldSchema, 'disease')}
                                        isFocus={false}
                                        sxStyle={{
                                            width: '620px',
                                            mr: '10px',
                                        }}
                                        handleBlur={() => {
                                            handleBlur(fieldName);
                                        }}
                                        handleChange={(event) => {
                                            handleChange(event, fieldName);
                                        }}
                                    />

                                    {handleDelete &&
                                        <Tooltip title="Remove field" placement="top-start">
                                            <IconButton
                                                sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                aria-label="remove field" component="label"
                                                onClick={(event) => {
                                                    handleDelete(event, fieldName);
                                                }}>
                                                <RemoveCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    }

                                </Fragment>
                                )

                            case 'geneSymbol':
                                return (<Fragment key={`${fieldName}-field-${index}`}>
                                    <GeneSymbolField
                                        key={`${fieldName}-field-${index}`}
                                        fieldName={'geneSymbol'}
                                        fieldLabel={findLabel(fieldSchema, 'geneSymbol')}
                                        currentValue={formFields.geneSymbol ? formFields.geneSymbol : ''}
                                        geneCurations={biocurations.genes}
                                        fieldErrors={fieldErrors}
                                        helpText={findHelpText(fieldSchema, 'geneSymbol')}
                                        isFocus={false}
                                        handleBlur={() => {
                                            handleBlur(fieldName);
                                        }}
                                        handleChange={(event) => {
                                            handleChange(event, fieldName);
                                        }}
                                    />

                                    {handleDelete &&
                                        <Tooltip title="Remove field" placement="top-start">
                                            <IconButton
                                                sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                aria-label="remove field" component="label"
                                                onClick={(event) => {
                                                    handleDelete(event, fieldName);
                                                }}>
                                                <RemoveCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    }

                                </Fragment>
                                )

                            case 'patientHPO':

                                return (<Fragment key={`${fieldName}-field-${index}`} >
                                    <PatientHPOField
                                        key={`${fieldName}-field-${index}`}
                                        fieldName={fieldName}
                                        fieldLabel={findLabel(fieldSchema, 'patientHPO')}
                                        currentValues={arrayExists(formFields.patientHPO) ? formFields.patientHPO : []}
                                        fieldErrors={fieldErrors}
                                        helpText={findHelpText(fieldSchema, 'patientHPO')}
                                        isFocus={false}
                                        loading={dataLoading}
                                        sxStyle={{
                                            width: 360,
                                            mb: '10px',
                                        }}
                                        handleBlur={() => {
                                            handleBlur(fieldName);
                                        }}
                                        handleChange={(event) => {
                                            handleChange(event, fieldName);
                                        }}
                                    />

                                    {handleDelete &&
                                        <Tooltip title="Remove field" placement="top-start">
                                            <IconButton
                                                sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                aria-label="remove field" component="label"
                                                onClick={(event) => {
                                                    handleDelete(event, fieldName);
                                                }}>
                                                <RemoveCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    }

                                </Fragment>
                                )

                            case 'variantObj':

                                return (<Fragment key={`${fieldName}-field-${index}`}>
                                    <VariantObjField
                                        key={`${fieldName}-field-${index}`}
                                        annotationType={annotationType}
                                        fieldSchema={fieldSchema}
                                        biocurations={biocurations}
                                        variantObj={fieldValue ? fieldValue : {}}
                                        isArrayElement={false}
                                        handleBlur={handleVariantObjBlur}
                                        handleDelete={handleVariantObjDelete}
                                        handleGDotLookup={handleGDotLookup}
                                    />

                                    {handleDelete &&
                                        <Tooltip title="Remove field" placement="top-start">
                                            <IconButton
                                                sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                aria-label="remove field" component="label"
                                                onClick={(event) => {
                                                    handleDelete(event, fieldName);
                                                }}>
                                                <RemoveCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    }

                                </Fragment>
                                );

                            case 'variantObjArray':

                                return (
                                    <Fragment key={`${fieldName}-field-${index}`}>

                                        <Grid container
                                            spacing={2}
                                            direction="row"
                                            justifyContent="flex-start"
                                            alignItems="flex-start"
                                        >
                                            <Grid size={{ xs: 12, sm: 12 }}>

                                                <VariantObjArrayField
                                                    key={`${fieldName}-field-${index}`}
                                                    annotationType={annotationType}
                                                    fieldErrors={fieldErrors}
                                                    fieldSchema={fieldSchema}
                                                    biocurations={biocurations}
                                                    variantObjArray={fieldValue ? fieldValue : []}
                                                    handleBlur={handleVariantObjBlur}
                                                    handleChange={handleChange}
                                                    handleAdd={handleVariantObjAdd}
                                                    handleDelete={handleVariantObjDelete}
                                                    handleGDotLookup={handleGDotLookup}
                                                />

                                                {handleDelete &&
                                                    <Tooltip title="Remove field" placement="top-start">
                                                        <IconButton
                                                            sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                            aria-label="remove field" component="label"
                                                            onClick={(event) => {
                                                                handleDelete(event, fieldName);
                                                            }}>
                                                            <RemoveCircleIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                }
                                            </Grid>
                                        </Grid>
                                    </Fragment>
                                );

                            case 'transcript':
                                return (<Fragment key={`${fieldName}-field-${index}`}>
                                    <TranscriptField
                                        key={`transcript-field-${index}`}
                                        fieldErrors={fieldErrors}
                                        className={''}
                                        fieldLabel={'Transcript'}
                                        fieldName={'transcript'}
                                        currentValue={formFields.transcript ? formFields.transcript : ''}
                                        options={biocurations.transcripts}
                                        helpText={findHelpText(fieldSchema, 'transcript')}
                                        isFocus={false}
                                        isRequired={false}
                                        sxStyle={{
                                            width: '250px',
                                            mr: '10px'
                                        }}
                                        handleBlur={() => {
                                            handleBlur('transcript');
                                        }}
                                        handleChange={(event) => {
                                            handleChange(event, 'transcript');
                                        }}
                                    />

                                    {handleDelete &&
                                        <Tooltip title="Remove field" placement="top-start">
                                            <IconButton
                                                sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                aria-label="remove field" component="label"
                                                onClick={(event) => {
                                                    handleDelete(event, 'transcript');
                                                }}>
                                                <RemoveCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    }

                                </Fragment>
                                )

                            default:
                                // render fields by type
                                fieldAttributes = fieldSchema[fieldName];

                                if (objectExists(fieldAttributes)) {
                                    const fieldType = fieldAttributes.type;
                                    const displayType = fieldAttributes.displayType;

                                    switch (fieldType) {

                                        case 'bool':

                                            return (
                                                <Fragment key={`${fieldName}-field-${index}`}>
                                                    <VeriomeBooleanField
                                                        key={`${fieldName}-field-${index}`}
                                                        fieldErrors={fieldErrors}
                                                        fieldLabel={findLabel(fieldSchema, fieldName)}
                                                        fieldName={fieldName}
                                                        currentValue={formFields[fieldName] ? formFields[fieldName] : false}
                                                        helpText={findHelpText(fieldSchema, fieldName)}
                                                        isDisabled={false}
                                                        isFocus={false}
                                                        isRequired={false}
                                                        sxStyle={{
                                                            width: findWidth(fieldSchema, fieldName),
                                                        }}
                                                        handleBlur={() => {
                                                            handleBlur(fieldName);
                                                        }}
                                                        handleChange={(event) => {
                                                            handleChange(event, fieldName);
                                                        }}
                                                    />

                                                    {handleDelete &&
                                                        <Tooltip title="Remove field" placement="top-start">
                                                            <IconButton
                                                                sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                                aria-label="remove field" component="label"
                                                                onClick={(event) => {
                                                                    handleDelete(event, fieldName);
                                                                }}>
                                                                <RemoveCircleIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    }

                                                </Fragment>
                                            );

                                        case 'string':

                                            if (displayType === 'select') {

                                                return (
                                                    <Fragment key={`${fieldName}-field-${index}`}>
                                                        <VeriomeSelectField
                                                            key={`${fieldName}-field-${index}`}
                                                            fieldErrors={fieldErrors}
                                                            className={''}
                                                            fieldLabel={findLabel(fieldSchema, fieldName)}
                                                            fieldName={fieldName}
                                                            currentValue={formFields[fieldName] ? formFields[fieldName] : ''}
                                                            options={findOptions(fieldSchema, fieldName)}
                                                            helpText={findHelpText(fieldSchema, fieldName)}
                                                            isFocus={false}
                                                            isRequired={false}
                                                            sxStyle={{
                                                                width: findWidth(fieldSchema, fieldName),
                                                                mr: '10px'
                                                            }}
                                                            handleBlur={() => {
                                                                handleBlur(fieldName);
                                                            }}
                                                            handleChange={(event) => {
                                                                handleChange(event, fieldName);
                                                            }}
                                                        />

                                                        {handleDelete &&
                                                            <Tooltip title="Remove field" placement="top-start">
                                                                <IconButton
                                                                    sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                                    aria-label="remove field" component="label"
                                                                    onClick={(event) => {
                                                                        handleDelete(event, fieldName);
                                                                    }}>
                                                                    <RemoveCircleIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        }

                                                    </Fragment>
                                                );

                                            } else if (displayType === 'number') {

                                                return (
                                                    <Fragment key={`${fieldName}-field-${index}`}>
                                                        <VeriomeNumberField
                                                            key={`${fieldName}-field-${index}`}
                                                            fieldErrors={fieldErrors}
                                                            fieldLabel={findLabel(fieldSchema, fieldName)}
                                                            fieldName={fieldName}
                                                            currentValue={formFields[fieldName] ? formFields[fieldName] : ''}
                                                            helpText={findHelpText(fieldSchema, fieldName)}
                                                            isFocus={false}
                                                            isRequired={false}
                                                            sxStyle={{
                                                                width: findWidth(fieldSchema, fieldName),
                                                                mr: '10px'
                                                            }}
                                                            handleBlur={() => {
                                                                handleBlur(fieldName);
                                                            }}
                                                            handleChange={(event) => {
                                                                handleChange(event, fieldName);
                                                            }}
                                                        />

                                                        {handleDelete &&
                                                            <Tooltip title="Remove field" placement="top-start">
                                                                <IconButton
                                                                    sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                                    aria-label="remove field" component="label"
                                                                    onClick={(event) => {
                                                                        handleDelete(event, fieldName);
                                                                    }}>
                                                                    <RemoveCircleIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        }
                                                    </Fragment>
                                                );

                                            } else {

                                                return (
                                                    <Fragment key={`${fieldName}-field-${index}`}>
                                                        <VeriomeTextField
                                                            key={`${fieldName}-field-${index}`}
                                                            fieldErrors={fieldErrors}
                                                            fieldLabel={findLabel(fieldSchema, fieldName)}
                                                            fieldName={fieldName}
                                                            currentValue={formFields[fieldName] ? formFields[fieldName] : ''}
                                                            helpText={findHelpText(fieldSchema, fieldName)}
                                                            isFocus={false}
                                                            isRequired={false}
                                                            isMultiline={false}
                                                            sxStyle={{
                                                                width: findWidth(fieldSchema, fieldName),
                                                                mr: '10px'
                                                            }}
                                                            handleBlur={() => {
                                                                handleBlur(fieldName);
                                                            }}
                                                            handleChange={(event) => {
                                                                handleChange(event, fieldName);
                                                            }}
                                                        />

                                                        {handleDelete &&
                                                            <Tooltip title="Remove field" placement="top-start">
                                                                <IconButton
                                                                    sx={{ color: red[900], marginLeft: '0px', marginTop: '10px' }}
                                                                    aria-label="remove field" component="label"
                                                                    onClick={(event) => {
                                                                        handleDelete(event, fieldName);
                                                                    }}>
                                                                    <RemoveCircleIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        }

                                                    </Fragment>
                                                );
                                            }

                                        default:
                                            console.error(`unhandled field: ${fieldName}`)
                                            return <span key={index} />
                                    }

                                } else {
                                    console.error(`fieldAttributes not found for field: ${fieldName}`)
                                }

                                return <span key={index} />
                        }
                    }

                })
            }
        </Grid >
    )
}

RenderAnnotationFields.propTypes = {
    annotationType: PropTypes.string,
    biocurations: PropTypes.object.isRequired,
    dataLoading: PropTypes.bool,
    diseaseOptions: PropTypes.array,
    fieldErrors: PropTypes.object,
    fieldSchema: PropTypes.array.isRequired,
    formFields: PropTypes.object.isRequired,
    handleBlur: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleDelete: PropTypes.func,
    handleVariantObjAdd: PropTypes.func.isRequired,
    handleVariantObjBlur: PropTypes.func.isRequired,
    handleVariantObjDelete: PropTypes.func.isRequired,
    handleGDotLookup: PropTypes.func.isRequired,
};

export default RenderAnnotationFields;